import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pmAdminApi, PM_ROLES } from '../../lib/pmApi';
import { startPortalPreview } from '../../lib/portalPreview';
import { startPmImpersonation } from '../../lib/pmImpersonation';
import { PmViewSideChooser } from '../../components/pm/PmViewSideChooser';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus, KeyRound, Trash2, Eye, Mail, Star, Lock, Unlock, Ban, CheckCircle2,
  Link2, Send, XCircle, History, Settings2, Copy,
} from 'lucide-react';

const emptyForm = { name: '', email: '', phone: '', role: 'property_manager', company_id: '', property_ids: [] };

const PmAccounts = () => {
  const { hasPermission, user } = useAuth();
  const canManage = hasPermission('manage_pm_portal_accounts');
  const canPreview = hasPermission('preview_pm_portal');
  const canFullAccess = ['super_admin', 'admin'].includes(user?.role);

  const [companies, setCompanies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [edit, setEdit] = useState(null);
  const [lastTemp, setLastTemp] = useState(null);
  const [linkBanner, setLinkBanner] = useState(null); // { title, url, email }
  const [manageId, setManageId] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { user, newEmail, reason }
  const [reasonModal, setReasonModal] = useState(null); // { user, kind: 'lock'|'disable', reason }
  const [auditModal, setAuditModal] = useState(null); // { user, rows }

  const load = () => Promise.all([
    pmAdminApi.companies.list(),
    pmAdminApi.properties.list(),
    pmAdminApi.users.list(),
  ]).then(([co, pr, us]) => { setCompanies(co); setProperties(pr); setUsers(us); });

  useEffect(() => { load(); }, []);

  // Deep-link from Companies → "Create Portal User" / "Go to Portal Accounts".
  // Prefills the New Account form's company when ?company=<id> is present.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get('company');
    if (cid) {
      setEdit(null);
      setForm((f) => ({ ...f, company_id: cid, property_ids: [] }));
    }
  }, [searchParams]);

  const propertiesForCompany = (cid) => properties.filter((p) => p.company_id === cid);
  const companyName = (cid) => companies.find((c) => c.id === cid)?.name || '';

  const toggleProp = (pid) => setForm((f) => ({
    ...f,
    property_ids: f.property_ids.includes(pid) ? f.property_ids.filter((x) => x !== pid) : [...f.property_ids, pid],
  }));

  const showLink = (title, resp, email) => {
    const token = resp.token;
    const url = `${window.location.origin}/pm/reset/${token}`;
    setLinkBanner({ title, url, email });
  };

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); toast.success('Link copied'); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  const save = async () => {
    try {
      if (edit) {
        await pmAdminApi.users.update(edit, form);
        setEdit(null); setForm(emptyForm); load();
        toast.success('Account updated');
      } else {
        const r = await pmAdminApi.users.create(form);
        setLastTemp({ email: r.email, password: r.temp_password });
        setForm(emptyForm); load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const startEdit = (u) => {
    setEdit(u.id);
    setForm({
      name: u.name, email: u.email, phone: u.phone || '',
      role: u.role, company_id: u.company_id || '',
      property_ids: u.property_ids || [],
    });
  };

  const wrap = (fn) => async (...args) => {
    try { await fn(...args); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const resetPw = wrap(async (u) => {
    if (!window.confirm(`Generate a temporary password for ${u.email}?`)) return;
    const r = await pmAdminApi.users.resetPw(u.id);
    setLastTemp({ email: u.email, password: r.temp_password });
    toast.success('Temporary password generated & emailed');
  });

  const sendResetLink = wrap(async (u) => {
    const r = await pmAdminApi.users.sendResetLink(u.id);
    showLink('Password reset link (valid 24h, single-use)', r, u.email);
    toast.success('Reset link emailed');
  });

  const invite = wrap(async (u) => {
    const r = await pmAdminApi.users.invite(u.id);
    showLink('Invite / set-password link (valid 24h, single-use)', r, u.email);
    load();
    toast.success('Invite emailed');
  });

  const cancelInvite = wrap(async (u) => {
    if (!window.confirm(`Cancel the outstanding invite for ${u.email}?`)) return;
    await pmAdminApi.users.cancelInvite(u.id);
    load(); toast.success('Invite canceled');
  });

  const setPrimary = wrap(async (u, next) => {
    await pmAdminApi.users.setPrimary(u.id, next);
    load(); toast.success(next ? 'Set as primary portal admin' : 'Removed primary status');
  });

  const enable = wrap(async (u) => { await pmAdminApi.users.enable(u.id); load(); toast.success('Account enabled'); });

  const unlock = wrap(async (u) => { await pmAdminApi.users.unlock(u.id); load(); toast.success('Account unlocked'); });

  const openAudit = wrap(async (u) => {
    const rows = await pmAdminApi.users.accountAudit(u.id);
    setAuditModal({ user: u, rows });
  });

  const submitEmail = wrap(async () => {
    const { user, newEmail, reason } = emailModal;
    await pmAdminApi.users.changeEmail(user.id, { new_email: newEmail, reason });
    setEmailModal(null); load(); toast.success('Login email updated');
  });

  const submitReason = wrap(async () => {
    const { user, kind, reason } = reasonModal;
    if (kind === 'lock') await pmAdminApi.users.lock(user.id, reason);
    else await pmAdminApi.users.disableWithReason(user.id, reason);
    setReasonModal(null); load();
    toast.success(kind === 'lock' ? 'Account locked' : 'Account disabled');
  });

  const viewTheirSide = (u) => startPortalPreview({
    id: u.id, name: u.name, email: u.email, role: u.role, context: companyName(u.company_id),
  });

  const fullAccess = (u) => startPmImpersonation({
    id: u.id, name: u.name, email: u.email, role: u.role, company: companyName(u.company_id),
  });

  const Badge = ({ color, label, testid }) => (
    <span data-testid={testid} className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded ${color}`}>{label}</span>
  );

  const Btn = ({ testid, onClick, icon: Icon, children, cls }) => (
    <button data-testid={testid} onClick={onClick}
      className={`text-xs border rounded px-2.5 h-8 inline-flex items-center gap-1.5 ${cls}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />} {children}
    </button>
  );

  return (
    <div data-testid="admin-pm-accounts" className="p-6 max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Portal Accounts</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Secure admin control over Property Manager logins — reset links, email changes, primary admin, lock &amp; disable.</p>
        </div>
        {canManage && (
          <button data-testid="pm-acct-new" onClick={() => { setEdit(null); setForm(emptyForm); }} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Account</button>
        )}
      </div>

      {!canManage && (
        <div className="bg-[#141414] border border-[#262626] rounded p-4 text-[#A3A3A3] text-sm">
          You have read-only access. Ask an admin for the <span className="text-white font-mono">manage_pm_portal_accounts</span> permission to change logins.
        </div>
      )}

      {lastTemp && (
        <div data-testid="pm-acct-temp-banner" className="bg-[#141414] border border-[#D4AF37]/40 rounded p-4">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">TEMP CREDENTIALS</p>
          <p className="text-white mt-1">Email: <span className="font-mono">{lastTemp.email}</span></p>
          <p className="text-white">Password: <span className="font-mono">{lastTemp.password}</span></p>
          <p className="text-[#A3A3A3] text-xs mt-1">User will be forced to change on first login. Also emailed.</p>
          <button onClick={() => setLastTemp(null)} className="text-[#525252] text-xs mt-2">Dismiss</button>
        </div>
      )}

      {linkBanner && (
        <div data-testid="pm-acct-link-banner" className="bg-[#141414] border border-cyan-700/40 rounded p-4">
          <p className="text-cyan-300 text-xs font-bold tracking-[0.2em]">{linkBanner.title.toUpperCase()}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">For {linkBanner.email} — emailed automatically. Copy to share manually:</p>
          <div className="flex items-center gap-2 mt-2">
            <input data-testid="pm-acct-link-value" readOnly value={linkBanner.url} className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-3 text-white text-xs font-mono" />
            <button data-testid="pm-acct-link-copy" onClick={() => copy(linkBanner.url)} className="text-cyan-300 border border-cyan-700/40 rounded px-3 h-9 inline-flex items-center gap-1.5 text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
          </div>
          <button onClick={() => setLinkBanner(null)} className="text-[#525252] text-xs mt-2">Dismiss</button>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-2">
          {users.map((u) => (
            <div key={u.id} data-testid={`pm-acct-row-${u.id}`} className={`bg-[#141414] border border-[#262626] rounded p-4 ${u.active === false ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                    {u.name}
                    <span className="text-[#525252] text-xs capitalize">{u.role.replace(/_/g, ' ')}</span>
                    {u.is_primary_portal_admin && <Badge testid={`pm-acct-badge-primary-${u.id}`} color="bg-[#D4AF37]/20 text-[#D4AF37]" label="PRIMARY" />}
                    {u.billing_locked && <Badge testid={`pm-acct-badge-locked-${u.id}`} color="bg-red-900/40 text-red-300" label="LOCKED" />}
                    {u.active === false && <Badge testid={`pm-acct-badge-disabled-${u.id}`} color="bg-[#404040] text-[#A3A3A3]" label="DISABLED" />}
                    {u.invite_status === 'pending' && <Badge testid={`pm-acct-badge-invite-${u.id}`} color="bg-cyan-900/40 text-cyan-300" label="INVITE PENDING" />}
                    {u.must_change_password && <Badge color="bg-amber-900/40 text-amber-300" label="PW CHANGE" />}
                  </p>
                  <p className="text-[#A3A3A3] text-xs">{u.email} · {companyName(u.company_id)} · {(u.property_ids || []).length} properties</p>
                  {u.billing_locked && u.billing_lock_reason && <p className="text-red-300 text-xs mt-1">Lock reason: {u.billing_lock_reason}</p>}
                </div>
                {(canPreview || canFullAccess) && (
                  <PmViewSideChooser
                    testidBase={`pm-acct-view-side-${u.id}`}
                    canPreview={canPreview}
                    canFullAccess={canFullAccess}
                    onReadOnly={() => viewTheirSide(u)}
                    onFullAccess={() => fullAccess(u)}
                    disabled={u.active === false || u.billing_locked === true}
                    disabledTitle="Disabled or locked accounts cannot be opened"
                  />
                )}
                {canManage && (
                  <Btn testid={`pm-acct-manage-${u.id}`} onClick={() => setManageId(manageId === u.id ? null : u.id)} icon={Settings2}
                    cls="text-white border-[#262626] hover:border-[#D4AF37]/40">Manage</Btn>
                )}
              </div>

              {canManage && manageId === u.id && (
                <div data-testid={`pm-acct-manage-panel-${u.id}`} className="mt-3 pt-3 border-t border-[#262626] flex flex-wrap gap-2">
                  <Btn testid={`pm-acct-edit-${u.id}`} onClick={() => startEdit(u)} icon={Settings2} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Edit profile</Btn>
                  <Btn testid={`pm-acct-changeemail-${u.id}`} onClick={() => setEmailModal({ user: u, newEmail: '', reason: '' })} icon={Mail} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Change email</Btn>
                  {u.is_primary_portal_admin
                    ? <Btn testid={`pm-acct-unprimary-${u.id}`} onClick={() => setPrimary(u, false)} icon={Star} cls="text-[#D4AF37] border-[#D4AF37]/40">Remove primary</Btn>
                    : <Btn testid={`pm-acct-setprimary-${u.id}`} onClick={() => setPrimary(u, true)} icon={Star} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Set as primary</Btn>}
                  <Btn testid={`pm-acct-resetlink-${u.id}`} onClick={() => sendResetLink(u)} icon={Link2} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Send reset link</Btn>
                  <Btn testid={`pm-acct-resetpw-${u.id}`} onClick={() => resetPw(u)} icon={KeyRound} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Temp password</Btn>
                  <Btn testid={`pm-acct-invite-${u.id}`} onClick={() => invite(u)} icon={Send} cls="text-cyan-300 border-cyan-700/40">{u.invite_status === 'pending' ? 'Resend invite' : 'Send invite'}</Btn>
                  {u.invite_status === 'pending' && <Btn testid={`pm-acct-cancelinvite-${u.id}`} onClick={() => cancelInvite(u)} icon={XCircle} cls="text-[#A3A3A3] border-[#262626] hover:text-red-300">Cancel invite</Btn>}
                  {u.active === false
                    ? <Btn testid={`pm-acct-enable-${u.id}`} onClick={() => enable(u)} icon={CheckCircle2} cls="text-green-300 border-green-700/40">Enable</Btn>
                    : <Btn testid={`pm-acct-disable-${u.id}`} onClick={() => setReasonModal({ user: u, kind: 'disable', reason: '' })} icon={Ban} cls="text-[#A3A3A3] border-[#262626] hover:text-red-300">Disable</Btn>}
                  {u.billing_locked
                    ? <Btn testid={`pm-acct-unlock-${u.id}`} onClick={() => unlock(u)} icon={Unlock} cls="text-green-300 border-green-700/40">Unlock (billing)</Btn>
                    : <Btn testid={`pm-acct-lock-${u.id}`} onClick={() => setReasonModal({ user: u, kind: 'lock', reason: '' })} icon={Lock} cls="text-red-300 border-red-700/40">Lock (billing)</Btn>}
                  <Btn testid={`pm-acct-audit-${u.id}`} onClick={() => openAudit(u)} icon={History} cls="text-[#A3A3A3] border-[#262626] hover:text-[#D4AF37]">Audit</Btn>
                </div>
              )}
            </div>
          ))}
          {users.length === 0 && <p className="text-[#525252]">No portal accounts yet.</p>}
        </div>

        {canManage && (
          <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded p-5 space-y-3 self-start">
            <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em]">{edit ? 'EDIT ACCOUNT' : 'NEW ACCOUNT'}</p>
            <input data-testid="pm-acct-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input data-testid="pm-acct-email" placeholder="Email" type="email" disabled={!!edit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm disabled:opacity-50" />
            {edit && <p className="text-[#525252] text-[11px]">To change the login email, use “Change email” in Manage — it preserves history and checks uniqueness.</p>}
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <select data-testid="pm-acct-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
              {PM_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select data-testid="pm-acct-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, property_ids: [] })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
              <option value="">Select company…</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {form.role !== 'asset_manager' && form.company_id && (
              <div>
                <p className="text-[#A3A3A3] text-xs mb-1">Assign properties (Asset Managers see all company-wide)</p>
                <div className="space-y-1 max-h-40 overflow-y-auto bg-[#0A0A0A] border border-[#262626] rounded p-2">
                  {propertiesForCompany(form.company_id).map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs text-white">
                      <input type="checkbox" data-testid={`pm-acct-assign-${p.id}`} checked={form.property_ids.includes(p.id)} onChange={() => toggleProp(p.id)} className="accent-[#D4AF37]" />
                      {p.name} <span className="text-[#525252]">{p.property_code}</span>
                    </label>
                  ))}
                  {propertiesForCompany(form.company_id).length === 0 && <p className="text-[#525252] text-xs">No properties for this company yet.</p>}
                </div>
              </div>
            )}
            <button data-testid="pm-acct-save" onClick={save} className="w-full bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB]">{edit ? 'Save changes' : 'Create account & email password'}</button>
            {edit && <button onClick={() => { setEdit(null); setForm(emptyForm); }} className="w-full text-[#525252] text-xs">Cancel</button>}
          </div>
        )}
      </div>

      {/* Change email modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="pm-acct-email-modal">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-lg p-6 space-y-4">
            <p className="text-white font-medium">Change login email</p>
            <p className="text-[#A3A3A3] text-sm">Current: <span className="font-mono">{emailModal.user.email}</span>. Updates this login only — sub-employees are not affected. Old email kept in history.</p>
            <input data-testid="pm-acct-email-new" type="email" placeholder="New login email" value={emailModal.newEmail} onChange={(e) => setEmailModal({ ...emailModal, newEmail: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input data-testid="pm-acct-email-reason" placeholder="Reason (optional)" value={emailModal.reason} onChange={(e) => setEmailModal({ ...emailModal, reason: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <div className="flex gap-2">
              <button data-testid="pm-acct-email-confirm" onClick={submitEmail} disabled={!emailModal.newEmail} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-50">Update email</button>
              <button onClick={() => setEmailModal(null)} className="px-4 text-[#A3A3A3] text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lock / disable reason modal */}
      {reasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="pm-acct-reason-modal">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-lg p-6 space-y-4">
            <p className="text-white font-medium">{reasonModal.kind === 'lock' ? 'Lock account (billing)' : 'Disable account'}</p>
            <p className="text-[#A3A3A3] text-sm">
              {reasonModal.kind === 'lock'
                ? 'A locked account cannot log in or view anything until you unlock it. On login they see “Error logging in — please contact NexFlow directly.”'
                : 'A disabled account is blocked at login and excluded from “View Their Side”.'}
            </p>
            <input data-testid="pm-acct-reason-input" placeholder="Reason" value={reasonModal.reason} onChange={(e) => setReasonModal({ ...reasonModal, reason: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <div className="flex gap-2">
              <button data-testid="pm-acct-reason-confirm" onClick={submitReason} className={`flex-1 h-10 rounded font-medium ${reasonModal.kind === 'lock' ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]'}`}>{reasonModal.kind === 'lock' ? 'Lock account' : 'Disable account'}</button>
              <button onClick={() => setReasonModal(null)} className="px-4 text-[#A3A3A3] text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Audit modal */}
      {auditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="pm-acct-audit-modal">
          <div className="w-full max-w-lg bg-[#141414] border border-[#262626] rounded-lg p-6 space-y-3 max-h-[80vh] overflow-y-auto">
            <p className="text-white font-medium">Account audit — {auditModal.user.name}</p>
            {auditModal.rows.length === 0 && <p className="text-[#525252] text-sm">No changes recorded yet.</p>}
            {auditModal.rows.map((r) => (
              <div key={r.id} className="border-b border-[#262626] pb-2">
                <p className="text-white text-sm">{r.action.replace('pm_account.', '').replace(/_/g, ' ')}</p>
                <p className="text-[#A3A3A3] text-xs">{r.actor_name || 'System'} · {new Date(r.created_at).toLocaleString()}</p>
                {r.details && Object.keys(r.details).length > 0 && (
                  <p className="text-[#525252] text-xs font-mono mt-0.5">{JSON.stringify(r.details)}</p>
                )}
              </div>
            ))}
            <button onClick={() => setAuditModal(null)} className="w-full text-[#A3A3A3] text-sm mt-2">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PmAccounts;
